"use client";

import { Dialog } from "@ark-ui/react/dialog";
import { Portal } from "@ark-ui/react/portal";
import { IconX } from "@decibeltrade/react-icons";
import { IconButton } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { JSX } from "react";
import { css } from "styled-system/css";

export interface DrawerRenderProps {
  close: () => void;
  isOpen: boolean;
}

export interface DrawerProps {
  /**
   * The element that should open the drawer when clicked.
   * The element should be an HTML `button` that can accept a ref.
   * You may need to wrap your component in React.forwardRef.
   */
  trigger: JSX.Element;
  /** A function that returns a JSX.Element to be rendered as the dialog content. */
  renderContent: (renderProps: DrawerRenderProps) => JSX.Element;
  /**
   * When `true`, the dialog content will only be mounted when the drawer is opened
   * and will unmount when the drawer is closed.
   *
   * This is useful as a performance optimization for drawers that have a lot of content or
   * content that is expensive to render.
   */
  mountOnlyWhenVisible?: boolean;
}

/** A component for opening a drawer window that appears on top of the main content. */
export function Drawer({ trigger, renderContent, mountOnlyWhenVisible = false }: DrawerProps) {
  return (
    <Dialog.Root
      modal
      restoreFocus
      lazyMount={mountOnlyWhenVisible}
      unmountOnExit={mountOnlyWhenVisible}
    >
      <Dialog.Context>
        {({ open, setOpen }) => (
          <>
            <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
            <Portal>
              <Dialog.Backdrop className={backdropStyles} />
              <Dialog.Positioner>
                <Dialog.Content className={contentStyles}>
                  {renderContent({ close: () => setOpen(false), isOpen: open })}
                </Dialog.Content>
              </Dialog.Positioner>
            </Portal>
          </>
        )}
      </Dialog.Context>
    </Dialog.Root>
  );
}

const backdropStyles = css({
  zIndex: "dialog",
  position: "fixed",
  inset: "0",
  bg: "dialog.backdrop",
  "&[data-state='open']": {
    animationStyle: "fade-in",
  },
  "&[data-state='closed']": {
    animationStyle: "fade-out",
  },
});

const contentStyles = css({
  zIndex: "dialog",
  position: "fixed",
  top: "0",
  bottom: "0",
  left: "0",
  transform: "translateX(-100%)",
  "&[data-state='open']": {
    animationStyle: "slide-in-from-left",
  },
  "&[data-state='closed']": {
    animationStyle: "slide-out-to-left",
  },
});

export function DrawerCloseButton({ close }: Pick<DrawerRenderProps, "close">) {
  const t = useTranslations("general");
  return (
    <div className={css({ position: "absolute", top: "[22px]", left: "24" })}>
      <IconButton
        size="lg"
        variant="text"
        onClick={close}
        ariaLabel={t("close")}
        className={css({ p: "12!", m: "-12" })}
      >
        <IconX />
      </IconButton>
    </div>
  );
}
